import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { supabase } from './config/supabase';
import Home from './pages/Home';
import BookNow from './pages/Book_now';
import Price from './pages/Price_list';
import Shop from './pages/Shop';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Returns from './pages/Returns';
import Account from './pages/Account';
import VoucherPage from './pages/VoucherPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminAuth from './components/AdminAuth';
import LogoHeader from './components/LogoHeader';
import MobileNav from './components/MobileNav';
import ScrollToTop from './components/ScrollToTop';
import PromoBanner from './components/PromoBanner';
import Footer from './components/Footer';
import './assets/styles/app.css';

const pageTransition = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] } },
};

function AppContent() {
  const location = useLocation();
  const isAdminPage = location.pathname === '/admin';
  const [session, setSession] = useState(null);
  const [role, setRole] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // A session alone does not grant admin access — clients have sessions too.
  // The role in `profiles` is the gate here, and RLS is the gate in the database.
  const applySession = useCallback(async (nextSession) => {
    setSession(nextSession);

    if (!nextSession) {
      setRole(null);
      setAuthLoading(false);
      return;
    }

    setAuthLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', nextSession.user.id)
      .maybeSingle();

    if (error) {
      console.error('Failed to load profile role:', error);
      setRole(null);
    } else {
      setRole(data?.role ?? 'client');
    }
    setAuthLoading(false);
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (active) applySession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active) applySession(session);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  useEffect(() => {
    if (isAdminPage) {
      document.documentElement.classList.add('admin-page');
      document.body.classList.add('admin-page');
    } else {
      document.documentElement.classList.remove('admin-page');
      document.body.classList.remove('admin-page');
    }
    return () => {
      document.documentElement.classList.remove('admin-page');
      document.body.classList.remove('admin-page');
    };
  }, [isAdminPage]);

  const handleAdminLogin = (newSession) => applySession(newSession);
  const handleAdminLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setRole(null);
  };

  return (
    <>
      <ScrollToTop />
      {!isAdminPage && <PromoBanner />}
      {!isAdminPage && <LogoHeader />}
      {!isAdminPage && <MobileNav />}
      <main className={`app-main${isAdminPage ? ' app-main--admin' : ''}`}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={pageTransition.initial}
            animate={pageTransition.animate}
            exit={pageTransition.exit}
            style={{ minHeight: '100%' }}
          >
            <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/price" element={<Price />} />
              <Route path="/book" element={<BookNow />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/returns" element={<Returns />} />
              {/* Public, and it has to be: the voucher email links here as the
                  version to print, and a gift gets forwarded to someone who has
                  never signed in. The code is the credential, and the lookup
                  behind it returns only what belongs on the voucher. */}
              <Route path="/voucher/:code" element={<VoucherPage />} />
              {/* Open to everyone: the page itself is the sign-in surface. */}
              <Route
                path="/account"
                element={<Account session={session} authLoading={authLoading} />}
              />
              <Route
                path="/admin"
                element={
                  authLoading ? (
                    <div className="admin-dashboard"><div className="loading">Loading...</div></div>
                  ) : !session ? (
                    <AdminAuth onLogin={handleAdminLogin} />
                  ) : role === 'admin' ? (
                    <AdminDashboard onLogout={handleAdminLogout} />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
      {!isAdminPage && <Footer />}
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
